import React, { useEffect, useState, useCallback } from "react";
import { useApi } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Inbox, CheckCircle2, XCircle, Eye } from "lucide-react";
import { fmtDateTime } from "@/lib/format";

export default function Approvals() {
  const { get, post } = useApi();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("pending");
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [notes, setNotes] = useState("");

  const load = useCallback(async () => {
    const r = await get(`/approvals?status=${tab}`);
    setItems(r.data || []);
  }, [get, tab]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (a) => {
    setSelected(a);
    setNotes("");
    setDetail(null);
    try {
      if (a.type === "communication") {
        const cs = await get(`/contacts/${a.contact_id}/communications`);
        const c = (cs.data || []).find(x => x.id === a.reference_id);
        setDetail({ type: "communication", data: c });
      } else if (a.type === "claim_package") {
        const r = await get(`/claim-packages/${a.reference_id}`);
        setDetail({ type: "claim_package", data: r.data });
      } else {
        setDetail({ type: a.type, data: null });
      }
    } catch (e) { console.error(e); }
  };

  const decide = async (decision) => {
    if (!selected) return;
    setBusy(true);
    try {
      await post(`/approvals/${selected.id}/decide`, { decision, notes });
      toast.success(decision === "approve" ? "Approved & sent" : "Rejected");
      setSelected(null);
      setDetail(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5">
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Approval queue</p>
        <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900 mt-1">
          Owner Approvals
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Nothing leaves this system without your explicit release. Review each item, then approve or reject.
        </p>
      </div>

      <div className="inline-flex bg-slate-100 rounded-lg p-1">
        {["pending", "approved", "rejected"].map(t => (
          <button
            key={t}
            data-testid={`approvals-tab-${t}`}
            onClick={() => setTab(t)}
            className={`px-4 py-1.5 text-sm font-semibold rounded-md transition ${
              tab === t ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"
            }`}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      <Card className="border-slate-200 p-0 overflow-hidden">
        {items.length === 0 ? (
          <div className="p-10 text-center text-slate-500">
            <Inbox className="h-10 w-10 mx-auto mb-3 text-slate-300" />
            <p className="font-semibold text-slate-700">No {tab} approvals</p>
            <p className="text-xs mt-1">When the agent stages a communication or claim package, it lands here.</p>
          </div>
        ) : (
          <table className="cw-table w-full">
            <thead>
              <tr>
                <th>Type</th>
                <th>Title</th>
                <th>Summary</th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id} data-testid={`approval-row-${a.id}`}>
                  <td>
                    <Badge variant="outline" className="text-xs">
                      {a.type.replace("_", " ")}
                    </Badge>
                  </td>
                  <td className="font-semibold text-slate-900">{a.title}</td>
                  <td className="text-slate-600 text-xs max-w-md truncate">{a.summary}</td>
                  <td className="text-xs text-slate-500 font-mono">{fmtDateTime(a.created_at)}</td>
                  <td className="text-right">
                    {a.status === "pending" ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openDetail(a)}
                        data-testid={`approval-review-${a.id}`}
                      >
                        <Eye className="h-3.5 w-3.5 mr-1" /> Review
                      </Button>
                    ) : (
                      <Button size="sm" variant="ghost" onClick={() => nav(`/contacts/${a.contact_id}`)}>
                        Contact →
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">{selected?.title}</DialogTitle>
          </DialogHeader>
          {detail?.type === "communication" && detail.data && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-xs">
                <Badge variant="outline">{detail.data.channel}</Badge>
                <span className="text-slate-500">Touchpoint #{detail.data.touchpoint}</span>
              </div>
              {detail.data.subject && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">Subject</p>
                  <p className="text-sm font-semibold">{detail.data.subject}</p>
                </div>
              )}
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">Body</p>
                <div className="p-3 bg-slate-50 border border-slate-200 rounded text-sm whitespace-pre-wrap max-h-64 overflow-auto">
                  {detail.data.body}
                </div>
              </div>
              {detail.data.compliance_checks?.length > 0 && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">Compliance</p>
                  <ul className="text-xs space-y-1">
                    {detail.data.compliance_checks.map((c, i) => (
                      <li key={`${c.check}-${i}`} className="flex items-start gap-2">
                        {c.ok ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 mt-0.5" /> : <XCircle className="h-3.5 w-3.5 text-red-600 mt-0.5" />}
                        <div>
                          <span className="font-semibold">{c.check}.</span>{" "}
                          <span className="text-slate-500">{c.note}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          {detail?.type === "claim_package" && detail.data && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3 text-xs font-mono">
                <div><span className="text-slate-500">Claimant:</span> {detail.data.contract_data.claimant_name}</div>
                <div><span className="text-slate-500">Case:</span> {detail.data.contract_data.case_number}</div>
                <div><span className="text-slate-500">County:</span> {detail.data.contract_data.county}</div>
                <div><span className="text-slate-500">State:</span> {detail.data.contract_data.state}</div>
                <div><span className="text-slate-500">Surplus:</span> ${detail.data.contract_data.estimated_surplus?.toLocaleString()}</div>
                <div><span className="text-slate-500">Fee %:</span> {detail.data.contract_data.contingency_fee_pct?.toFixed(0)}%</div>
                <div><span className="text-slate-500">Est. Fee:</span> ${detail.data.estimated_fee?.toLocaleString()}</div>
                <div><span className="text-slate-500">Net:</span> ${detail.data.estimated_net?.toLocaleString()}</div>
              </div>
              <div className="p-3 bg-slate-50 border border-slate-200 rounded text-xs">
                <p className="font-semibold mb-1">State disclosure:</p>
                <p className="text-slate-700">{detail.data.contract_data.state_disclosure}</p>
              </div>
            </div>
          )}

          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">Notes (optional)</p>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Reason for rejection or additional context…"
              className="text-sm"
              data-testid="approval-notes-input"
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSelected(null)} data-testid="approval-cancel-btn">
              Cancel
            </Button>
            <Button
              variant="outline"
              className="border-red-300 text-red-700 hover:bg-red-50"
              onClick={() => decide("reject")}
              disabled={busy}
              data-testid="approval-reject-btn"
            >
              <XCircle className="h-4 w-4 mr-1" /> Reject
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={() => decide("approve")}
              disabled={busy}
              data-testid="approval-approve-btn"
            >
              <CheckCircle2 className="h-4 w-4 mr-1" /> Approve & Release
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
